import React, { useEffect, useState } from 'react';
import { Search, X, Mail, MapPin, Award, Briefcase, ArrowRight, Zap, ChevronDown } from 'lucide-react';
import { supabase } from '../supabaseClient';
import PageHeader from './PageHeader';
import './AdvocateList.css';

// ─── Custom Dropdown Component ────────────────────────────────────────────────
const CustomDropdown = ({ label, icon: Icon, options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = value || placeholder;

  return (
    <div className="filter-group" ref={dropdownRef}>
      <label className="filter-label">
        <Icon size={16} className="filter-label-icon" />
        {label}
      </label>
      <div className={`custom-select-wrapper ${isOpen ? 'is-open' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <div className="custom-select-trigger">
          <span className={!value ? 'placeholder-text' : ''}>{selectedLabel}</span>
          <ChevronDown size={18} className={`select-arrow ${isOpen ? 'rotate' : ''}`} />
        </div>
        {isOpen && (
          <div className="custom-options">
            <div
              className={`custom-option ${!value ? 'selected' : ''}`}
              onClick={() => { onChange(''); setIsOpen(false); }}
            >
              {placeholder}
            </div>
            {options.map((opt) => (
              <div
                key={opt}
                className={`custom-option ${value === opt ? 'selected' : ''}`}
                onClick={() => { onChange(opt); setIsOpen(false); }}
              >
                {opt}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdvocateList({ onNavigate, user, initialSelectedAdvocate }) {
  const [advocates, setAdvocates] = useState([]);
  const [filteredAdvocates, setFilteredAdvocates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [practiceFilter, setPracticeFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAdvocate, setSelectedAdvocate] = useState(null);
  const [practiceOptions, setPracticeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [activeTab, setActiveTab] = useState('about');
  const [updatingFilters, setUpdatingFilters] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
  const [advocateRatings, setAdvocateRatings] = useState({});
  const [startingChat, setStartingChat] = useState(null);

  // Normalize practice areas data
  const normalizePracticeAreas = (pa) => {
    if (!pa) return [];
    if (Array.isArray(pa)) return pa.map(String).map(s => s.trim()).filter(Boolean);
    if (typeof pa === 'string') {
      try {
        const parsed = JSON.parse(pa);
        if (Array.isArray(parsed)) return parsed.map(String).map(s => s.trim()).filter(Boolean);
      } catch (e) {
        // Continue to split by comma
      }
      return pa.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };

  // Helper to fetch data
  const fetchAdvocatesFromDb = async () => {
    try {
      const { data, error } = await supabase
        .from('advocates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const list = (data || []).map(a => ({
        ...a,
        practice_areas: normalizePracticeAreas(a.practice_areas)
      }));
      setAdvocates(list);
      setFilteredAdvocates(list);

      // Extract unique practice areas
      const practices = new Set();
      const locations = new Set();
      list.forEach(a => {
        a.practice_areas.forEach(p => practices.add(p));
        if (a.location) locations.add(a.location);
      });
      setPracticeOptions(Array.from(practices).sort());
      setLocationOptions(Array.from(locations).sort());
      // Load ratings for each advocate
      const { data: ratingsData } = await supabase.from('ratings').select('lawyer_id, stars');
      if (ratingsData) {
        const ratingsMap = {};
        ratingsData.forEach(r => {
          if (!ratingsMap[r.lawyer_id]) ratingsMap[r.lawyer_id] = { total: 0, count: 0 };
          ratingsMap[r.lawyer_id].total += r.stars;
          ratingsMap[r.lawyer_id].count += 1;
        });
        setAdvocateRatings(ratingsMap);
      }
    } catch (err) {
      console.error('Error fetching advocates:', err);
      setError(err.message || 'Failed to load advocates');
    } finally {
      setLoading(false);
    }
  };

  // Fetch advocates data
  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchAdvocatesFromDb();
  }, []); // Initial load only

  // Separate effect to handle the auto-open so it's more reliable
  useEffect(() => {
    if (initialSelectedAdvocate) {
      console.log("Auto-opening advocate profile:", initialSelectedAdvocate.name);
      setSelectedAdvocate(initialSelectedAdvocate);
      setActiveTab('about');
    }
  }, [initialSelectedAdvocate]);

  // Add a manual refresh function
  const refreshAdvocates = () => {
    setLoading(true);
    setError(null);
    fetchAdvocatesFromDb();
  };

  // Filter advocates based on search and all filters
  useEffect(() => {
    let filtered = advocates;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.location?.toLowerCase().includes(q) ||
        a.bio?.toLowerCase().includes(q) ||
        a.practice_areas?.some(p => p.toLowerCase().includes(q))
      );
    }

    if (practiceFilter) {
      filtered = filtered.filter(a =>
        a.practice_areas.includes(practiceFilter)
      );
    }

    if (locationFilter) {
      filtered = filtered.filter(a =>
        a.location === locationFilter
      );
    }

    if (experienceFilter) {
      filtered = filtered.filter(a => {
        const exp = parseInt(a.experience) || 0;
        if (experienceFilter === '5+') return exp >= 5;
        if (experienceFilter === '10+') return exp >= 10;
        if (experienceFilter === '15+') return exp >= 15;
        return true;
      });
    }

    setFilteredAdvocates(filtered);
  }, [searchQuery, practiceFilter, locationFilter, experienceFilter, advocates]);

  const applyFilters = () => {
    setUpdatingFilters(true);
    setTimeout(() => setUpdatingFilters(false), 300);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPracticeFilter('');
    setLocationFilter('');
    setExperienceFilter('');
  };

  const handleContactClick = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const handleAdvocateMatching = () => {
    if (onNavigate) {
      onNavigate('advocate-matching');
    }
  };

  const handleProfileClick = (advocate) => {
    setSelectedAdvocate(advocate);
    setActiveTab('about');
  };

  const startConversation = async (advocate) => {
    if (!user?.id) { alert('Please log in to send messages.'); return; }
    setStartingChat(advocate.id);
    try {
      // Use user_id stored directly on advocate row (fastest)
      let lawyerId = advocate.user_id || null;

      // Fallback: look up by email in user_profiles
      if (!lawyerId && advocate.email) {
        const { data: lawyerProfile } = await supabase
          .from('user_profiles').select('id').eq('email', advocate.email).single();
        lawyerId = lawyerProfile?.id || null;
      }

      if (!lawyerId) {
        alert('This advocate has not completed their account setup yet.');
        return;
      }

      if (lawyerId === user.id) {
        alert('You cannot message yourself.');
        return;
      }

      // Find existing conversation or create new one
      const { data: existing } = await supabase.from('conversations')
        .select('id').eq('client_id', user.id).eq('lawyer_id', lawyerId).maybeSingle();
      if (existing) {
        onNavigate('messages', existing.id);
      } else {
        const { data: newConv, error } = await supabase.from('conversations')
          .insert([{ client_id: user.id, lawyer_id: lawyerId }]).select().single();
        if (error) throw error;
        if (newConv) onNavigate('messages', newConv.id);
      }
    } catch (err) {
      console.error(err);
      alert('Could not start conversation: ' + err.message);
    } finally {
      setStartingChat(null);
    }
  };

  const getStarDisplay = (advocateId) => {
    const r = advocateRatings[advocateId];
    if (!r || r.count === 0) return { avg: 0, count: 0 };
    return { avg: (r.total / r.count).toFixed(1), count: r.count };
  };

  const closeModal = () => {
    setSelectedAdvocate(null);
  };

  if (loading) {
    return (
      <div className="advocates-page">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6B7280' }}>
          <div style={{ fontSize: '18px', marginBottom: '16px' }}>Loading advocates...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="advocates-page">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#FF6B6B' }}>
          <div style={{ fontSize: '18px' }}>Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="advocates-page">
      {/* Header */}
      <PageHeader
        title="Advocate Profiles"
        subtitle="Browse our experienced advocates to find the right match for your case"
        onBack={() => onNavigate && onNavigate('dashboard')}
        actionButton={
          <button className="advocate-matching-btn" onClick={() => onNavigate && onNavigate('recommend')}>
            <Zap size={18} />
            AI Matching
            <ArrowRight size={18} />
          </button>
        }
      />

      {/* Search & Filter Controls */}
      <div className="advocates-controls">
        <div className="controls-wrapper">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, email, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search size={18} className="search-icon" />
          </div>
        </div>

        {/* Advanced Filter Section */}
        <div className="filter-section">
          <div className="filter-group-container">
            <CustomDropdown
              label="Location"
              icon={MapPin}
              options={locationOptions}
              value={locationFilter}
              onChange={setLocationFilter}
              placeholder="All Locations"
            />

            <CustomDropdown
              label="Practice Area"
              icon={Briefcase}
              options={practiceOptions}
              value={practiceFilter}
              onChange={setPracticeFilter}
              placeholder="All Practice Areas"
            />

            <CustomDropdown
              label="Experience"
              icon={Award}
              options={['5+', '10+', '15+']}
              value={experienceFilter}
              onChange={setExperienceFilter}
              placeholder="Any Experience"
            />
          </div>

          <div className="filter-actions">
            <button className="filter-action-btn apply-filters-btn" onClick={applyFilters}>
              {updatingFilters && <span className="filter-spinner" />}
              {updatingFilters ? 'Updating...' : 'Apply Filters'}
            </button>
            <button className="filter-action-btn clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="advocates-content">
        {filteredAdvocates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon" style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
            <div className="empty-state-title" style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b' }}>No advocates found</div>
            <div className="empty-state-text" style={{ color: '#64748b' }}>
              Try adjusting your search or filter criteria
            </div>
          </div>
        ) : (
          <div className="advocates-grid">
            {filteredAdvocates.map((advocate) => (
              <div key={advocate.id} className="advocate-card">
                <div className="advocate-image-container">
                  <img
                    src={advocate.photo_path || 'https://via.placeholder.com/120?text=No+Photo'}
                    alt={advocate.name}
                    className="advocate-image"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/120?text=No+Photo';
                    }}
                  />
                </div>

                <h3 className="advocate-name">{advocate.name}</h3>
                <p className="advocate-specialty">Legal Professional</p>

                {/* Location */}
                {advocate.location && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                    <MapPin size={14} color="#6C3483" />
                    <span>{advocate.location}</span>
                  </div>
                )}

                {/* Bio Section */}
                <div className="bio-section">
                  <h4 className="section-label">Professional Bio</h4>
                  <p className="advocate-bio">
                    {advocate.bio || 'Dedicated legal professional with expertise in multiple practice areas.'}
                  </p>
                </div>

                {/* Stats */}
                <div className="advocate-stats">
                  <div className="stat-item">
                    <div className="stat-label">Experience</div>
                    <div className="stat-value">{advocate.experience ? `${advocate.experience} yrs` : 'N/A'}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Bar Number</div>
                    <div className="stat-value">{advocate.bar_number?.substring(0, 8) || 'Active'}</div>
                  </div>
                </div>

                {/* Practice Areas */}
                <div className="practice-areas-section">
                  <h4 className="section-label">Practice Areas</h4>
                  <div className="practice-areas-container">
                    {advocate.practice_areas && advocate.practice_areas.length > 0 ? (
                      advocate.practice_areas.slice(0, 3).map((area) => (
                        <span key={area} className="practice-badge">
                          {area}
                        </span>
                      ))
                    ) : (
                      <span className="practice-badge">General Practice</span>
                    )}
                    {advocate.practice_areas?.length > 3 && (
                      <span className="practice-badge">+{advocate.practice_areas.length - 3} more</span>
                    )}
                  </div>
                </div>

                {/* Rating & Pricing */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  {(() => {
                    const { avg, count } = getStarDisplay(advocate.user_id || advocate.id);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                        <span style={{ color: '#F39C12', fontSize: '14px' }}>★</span>
                        <span style={{ color: '#1e293b', fontWeight: 700 }}>{avg > 0 ? avg : 'N/A'}</span>
                        <span style={{ color: '#94a3b8' }}>({count})</span>
                      </div>
                    );
                  })()}

                  {(advocate.price_min || advocate.price_max) && (
                    <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: 800 }}>
                      PKR {advocate.price_min?.toLocaleString() || '?'}
                    </div>
                  )}
                </div>

                <div className="card-buttons">
                  <button
                    className="btn-primary"
                    onClick={() => handleProfileClick(advocate)}
                  >
                    View Profile
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => startConversation(advocate)}
                    disabled={startingChat === advocate.id}
                  >
                    {startingChat === advocate.id ? '...' : 'Contact'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile Modal */}
      {selectedAdvocate && (
        <div className="profile-modal" onClick={closeModal}>
          <div className="profile-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal}>
              <X size={20} />
            </button>

            {/* Modal Header */}
            <div className="modal-header">
              <img
                src={selectedAdvocate.photo_path || 'https://via.placeholder.com/140?text=No+Photo'}
                alt={selectedAdvocate.name}
                className="modal-header-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/140?text=No+Photo';
                }}
              />
              <h2 className="modal-header-name">{selectedAdvocate.name}</h2>
              <p className="modal-header-specialty">Legal Professional</p>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              {/* Tab Navigation */}
              <div className="modal-tabs">
                <button
                  className={`modal-tab-btn ${activeTab === 'about' ? 'active' : ''}`}
                  onClick={() => setActiveTab('about')}
                >
                  <Mail size={16} className="modal-tab-icon" />
                  About Me
                </button>
                <button
                  className={`modal-tab-btn ${activeTab === 'experience' ? 'active' : ''}`}
                  onClick={() => setActiveTab('experience')}
                >
                  <Briefcase size={16} className="modal-tab-icon" />
                  Experience
                </button>
              </div>

              {/* About Tab */}
              <div className={`modal-tab-content ${activeTab === 'about' ? 'active' : ''}`}>
                <div className="profile-section">
                  <h3 className="profile-section-title">About Me</h3>
                  <p className="profile-section-content">
                    {selectedAdvocate.bio || 'Dedicated legal professional with extensive experience in providing expert legal counsel.'}
                  </p>
                </div>

                {/* Details Grid */}
                <div className="profile-details-grid">
                  <div className="detail-item">
                    <div className="detail-label">
                      <MapPin size={14} style={{ display: 'inline', marginRight: '6px' }} />
                      Location
                    </div>
                    <div className="detail-value">{selectedAdvocate.location || 'Not specified'}</div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <Briefcase size={14} style={{ display: 'inline', marginRight: '6px' }} />
                      Experience
                    </div>
                    <div className="detail-value">{selectedAdvocate.experience ? `${selectedAdvocate.experience} years` : 'N/A'}</div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <Award size={14} style={{ display: 'inline', marginRight: '6px' }} />
                      Bar Number
                    </div>
                    <div className="detail-value">{selectedAdvocate.bar_number || 'Active'}</div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <Mail size={14} style={{ display: 'inline', marginRight: '6px' }} />
                      Email
                    </div>
                    <div className="detail-value" style={{ fontSize: '14px', wordBreak: 'break-all' }}>
                      {selectedAdvocate.email}
                    </div>
                  </div>
                </div>
              </div>

              {/* Experience Tab */}
              <div className={`modal-tab-content ${activeTab === 'experience' ? 'active' : ''}`}>
                <div className="profile-section">
                  <h3 className="profile-section-title">Practice Areas</h3>
                  <div className="practice-areas-list">
                    {selectedAdvocate.practice_areas && selectedAdvocate.practice_areas.length > 0 ? (
                      selectedAdvocate.practice_areas.map((area) => (
                        <span key={area} className="practice-badge-large">
                          {area}
                        </span>
                      ))
                    ) : (
                      <span className="practice-badge-large">General Practice</span>
                    )}
                  </div>
                </div>

                <div className="profile-section">
                  <h3 className="profile-section-title">Professional Background</h3>
                  <p className="profile-section-content">
                    {selectedAdvocate.experience || '10+ years of professional legal experience'} with a proven track record of successful case outcomes and client satisfaction.
                  </p>
                </div>
              </div>

              {/* Contact Section */}
              <div className="modal-actions">
                <button
                  className="modal-btn modal-btn-primary"
                  onClick={() => startConversation(selectedAdvocate)}
                  disabled={startingChat === selectedAdvocate?.id}
                >
                  {startingChat === selectedAdvocate?.id ? 'Opening...' : 'Contact'}
                </button>
                <button
                  className="modal-btn modal-btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}